/**
 * Badges Section with GSAP ScrollTrigger
 * Handles 3-state transitions on desktop with pinned scrolling
 * Mobile: Shows all states in column layout
 */

"use strict";

function initBadgesMobileSection() {
  // Mobile section now uses relative positioning - all states visible
  // No observer or scroll tracking needed
      return;
}

function initBadgesSection() {
  const section = document.querySelector(
    '.badges-desktop[id^="badges-section-"]'
  );
  if (!section) return;

  const isMobile = window.innerWidth <= 999;

  // Get all badge items and image wrapper
  const leftItems = section.querySelectorAll(".badge-item-left");
  const rightItems = section.querySelectorAll(".badge-item-right");
  const imageWrapper = section.querySelector(".badges-image-wrapper");

  // Mobile: Don't initialize desktop section
  if (isMobile) {
    return;
  }

  // Desktop: Initialize state 1
  function setActiveState(state) {
    // Remove active class from all items
    leftItems.forEach((item) => item.classList.remove("active"));
    rightItems.forEach((item) => item.classList.remove("active"));

    // Add active class to current state items
    const leftItem = section.querySelector(
      `.badge-item-left[data-state="${state}"]`
    );
    const rightItem = section.querySelector(
      `.badge-item-right[data-state="${state}"]`
    );

    if (leftItem) {
      leftItem.classList.add("active");
    }
    if (rightItem) {
      rightItem.classList.add("active");
    }
  }

  // Initialize with state 1
  setActiveState(1);

  // Wait for GSAP to be available
  if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") {
    // Retry after a short delay
    setTimeout(initBadgesSection, 100);
    return;
  }

  // Register ScrollTrigger plugin
  gsap.registerPlugin(ScrollTrigger);

  // Set initial state - all items hidden except state 1
  const allItems = [...leftItems, ...rightItems];
  allItems.forEach((item) => {
    const state = parseInt(item.getAttribute("data-state"));

    // State 1 items are visible initially, others are hidden
    if (state === 1) {
      gsap.set(item, { opacity: 1, visibility: "visible" });
    } else {
      gsap.set(item, { opacity: 0, visibility: "hidden" });
    }
  });

  // Set initial transform for image wrapper and animate to -63vh
  if (imageWrapper) {
    // Set initial position (start from 0% or slightly above)
    gsap.set(imageWrapper, { transform: "translate(-50%, 0%)" });

    // Animate to -66vh after preloader finishes or immediately if already finished
    function animateImageWrapper() {
      gsap.to(imageWrapper, {
        transform: "translate(-50%, -63vh)",
        duration: 1,
        ease: "power3.out",
      });
    }

    // Check if preloader is already finished
    const preloader = document.getElementById("preloader");
    const preloaderSection = document.getElementById("preloader-section");
    const isPreloaderHidden =
      (!preloader ||
        preloader.classList.contains("hidden") ||
        !document.body.contains(preloader)) &&
      (!preloaderSection ||
        preloaderSection.classList.contains("revealed") ||
        preloaderSection.style.display === "none" ||
        !document.body.contains(preloaderSection));

    if (isPreloaderHidden || (!preloader && !preloaderSection)) {
      // Preloader already finished, animate immediately with small delay
      setTimeout(animateImageWrapper, 200);
    } else {
      // Wait for preloader to finish
      document.addEventListener(
        "preloaderFinished",
        () => {
          setTimeout(animateImageWrapper, 200);
        },
        { once: true }
      );
    }
  }

  // Progress bar removed
  let progressBar = null;

  // Create ScrollTrigger for state transitions (4 states)
  // New state structure:
  // State 1: left badge1, right none
  // State 2: left badge2, right none
  // State 3: left badge2, right badge3
  // State 4: left badge4, right badge3
  ScrollTrigger.create({
    trigger: section,
    start: "top top",
    end: "+=400%", // 4x viewport height for 4 states
    pin: true,
    anticipatePin: 1,
    onUpdate: (self) => {
      const progress = self.progress;

      // Define transition points for 4 states
      // State 1 → State 2: 0-20% (transition), 20-25% (hold 5%)
      // State 2 → State 3: 25-45% (transition), 45-50% (hold 5%)
      // State 3 → State 4: 50-70% (transition), 70-75% (hold 5%)
      // State 4: 75-95% (transition/continue), 95-100% (hold)
      const state1End = 0.2; // State 1 → State 2 transition ends at 20%
      const state2HoldStart = 0.2; // State 2 hold period starts at 20%
      const state2HoldEnd = 0.25; // State 2 hold period ends at 25%
      const state2End = 0.45; // State 2 → State 3 transition ends at 45%
      const state3HoldStart = 0.45; // State 3 hold period starts at 45%
      const state3HoldEnd = 0.5; // State 3 hold period ends at 50%
      const state3End = 0.7; // State 3 → State 4 transition ends at 70%
      const state4HoldStart = 0.7; // State 4 hold period starts at 70%
      const state4HoldEnd = 0.75; // State 4 hold period ends at 75%
      const state4End = 0.95; // State 4 continues until 95%

      // Calculate opacity for each state (fade in/out)
      let leftState1Opacity,
        leftState2Opacity,
        leftState3Opacity,
        leftState4Opacity;
      let rightState1Opacity,
        rightState2Opacity,
        rightState3Opacity,
        rightState4Opacity;
      let imageTransformY; // Transform Y value in vh units for image wrapper
      // Base starting position: -63vh (changed from -66vh)
      const startPosition = -63; // Starting position in vh units

      if (progress <= state1End) {
        // State 1 → State 2: 0-20% - Fade out left badge1, fade in left badge2
        const state1Progress = progress / state1End;
        leftState1Opacity = 1 - state1Progress; // Fade out badge1
        leftState2Opacity = state1Progress; // Fade in badge2
        leftState3Opacity = 0;
        leftState4Opacity = 0;
        rightState1Opacity = 0; // Right none (empty)
        rightState2Opacity = 0; // Right none (empty)
        rightState3Opacity = 0;
        rightState4Opacity = 0;
        // Image transform: -63vh to -70vh (state 2) - difference of 7vh
        imageTransformY = startPosition - state1Progress * 7; // -63vh to -70vh
      } else if (progress <= state2HoldEnd) {
        // State 2: 20-25% - Hold state 2 (5%)
        leftState1Opacity = 0;
        leftState2Opacity = 1; // badge2 visible
        leftState3Opacity = 0;
        leftState4Opacity = 0;
        rightState1Opacity = 0; // Right none (empty)
        rightState2Opacity = 0; // Right none (empty)
        rightState3Opacity = 0;
        rightState4Opacity = 0;
        // Image transform: stay at -70vh
        imageTransformY = -70;
      } else if (progress <= state2End) {
        // State 2 → State 3: 25-45% - Fade in right badge3 (left badge2 stays)
        const state2Progress =
          (progress - state2HoldEnd) / (state2End - state2HoldEnd);
        leftState1Opacity = 0;
        leftState2Opacity = 1; // badge2 stays visible
        leftState3Opacity = 0;
        leftState4Opacity = 0;
        rightState1Opacity = 0;
        rightState2Opacity = 0;
        rightState3Opacity = state2Progress; // Fade in badge3
        rightState4Opacity = 0;
        // Image transform: -70vh to -80vh (state 3) - difference of 10vh
        imageTransformY = -70 - state2Progress * 10; // -70vh to -80vh
      } else if (progress <= state3HoldEnd) {
        // State 3: 45-50% - Hold state 3 (5%)
        leftState1Opacity = 0;
        leftState2Opacity = 1; // badge2 visible
        leftState3Opacity = 0;
        leftState4Opacity = 0;
        rightState1Opacity = 0;
        rightState2Opacity = 0;
        rightState3Opacity = 1; // badge3 visible
        rightState4Opacity = 0;
        // Image transform: stay at -80vh
        imageTransformY = -80;
      } else if (progress <= state3End) {
        // State 3 → State 4: 50-70% - Fade out left badge2, fade in left badge4 (right badge3 stays)
        const state3Progress =
          (progress - state3HoldEnd) / (state3End - state3HoldEnd);
        leftState1Opacity = 0;
        leftState2Opacity = 1 - state3Progress; // Fade out badge2
        leftState3Opacity = 0;
        leftState4Opacity = state3Progress; // Fade in badge4
        rightState1Opacity = 0;
        rightState2Opacity = 0;
        rightState3Opacity = 1; // badge3 stays visible
        rightState4Opacity = 0;
        // Image transform: -80vh to -120vh (state 4) - difference of 40vh
        imageTransformY = -80 - state3Progress * 40; // -80vh to -120vh
      } else if (progress <= state4HoldEnd) {
        // State 4: 70-75% - Hold state 4 (5%)
        leftState1Opacity = 0;
        leftState2Opacity = 0;
        leftState3Opacity = 0;
        leftState4Opacity = 1; // badge4 visible
        rightState1Opacity = 0;
        rightState2Opacity = 0;
        rightState3Opacity = 1; // badge3 visible
        rightState4Opacity = 0;
        // Image transform: stay at -120vh
        imageTransformY = -120;
      } else if (progress <= state4End) {
        // State 4: 75-95% - Continue state 4
        leftState1Opacity = 0;
        leftState2Opacity = 0;
        leftState3Opacity = 0;
        leftState4Opacity = 1; // badge4 visible
        rightState1Opacity = 0;
        rightState2Opacity = 0;
        rightState3Opacity = 1; // badge3 visible
        rightState4Opacity = 0;
        // Image transform: stay at -120vh
        imageTransformY = -120;
      } else {
        // State 4: 95-100% - Hold state 4 until end
        leftState1Opacity = 0;
        leftState2Opacity = 0;
        leftState3Opacity = 0;
        leftState4Opacity = 1; // badge4 visible
        rightState1Opacity = 0;
        rightState2Opacity = 0;
        rightState3Opacity = 1; // badge3 visible
        rightState4Opacity = 0;
        imageTransformY = -120;
      }

      // Update all badge items with fade in/out animations
      // New images should appear in front of old ones (higher z-index)
      const allItems = [...leftItems, ...rightItems];
      allItems.forEach((item) => {
        const state = parseInt(item.getAttribute("data-state"));
        const isLeft = item.classList.contains("badge-item-left");
        let opacity = 0;
        let zIndex = 1; // Default z-index

        // Determine opacity based on state and side
        if (isLeft) {
          if (state === 1) opacity = leftState1Opacity;
          else if (state === 2) opacity = leftState2Opacity;
          else if (state === 3) opacity = leftState3Opacity;
          else if (state === 4) opacity = leftState4Opacity;
        } else {
          if (state === 1) opacity = rightState1Opacity;
          else if (state === 2) opacity = rightState2Opacity;
          else if (state === 3) opacity = rightState3Opacity;
          else if (state === 4) opacity = rightState4Opacity;
        }

        // Set z-index: higher state number = higher z-index (appears in front)
        // This ensures new images appear in front of old ones during transitions
        zIndex = state;

        // Apply fade in/out with smooth transition and z-index
        // Small duration for smooth fading while staying responsive to scroll
        gsap.to(item, {
          opacity: opacity,
          visibility: opacity > 0 ? "visible" : "hidden",
          zIndex: zIndex,
          duration: 0.15,
          ease: "power1.out",
        });
      });

      // Update image wrapper transform with smooth easing
      if (imageWrapper) {
        gsap.to(imageWrapper, {
          transform: `translate(-50%, ${imageTransformY}vh)`,
          duration: 0.4,
          ease: "expoScale(0.5,7,none)", // Smooth slowdown with slight overshoot effect
        });
      }

      // Progress bar removed
    },
  });

  // Handle window resize
  let resizeTimer;
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      ScrollTrigger.refresh();
      if (window.innerWidth <= 999) {
        // Mobile: no initialization needed
      } else {
        // Desktop: reinitialize
        ScrollTrigger.getAll().forEach((trigger) => {
          if (trigger.trigger === section) {
            trigger.kill();
          }
        });
        initBadgesSection();
      }
    }, 250);
  });
}

// Initialize when DOM is ready
function initialize() {
  // Use global scroll-to-top function if available, otherwise execute immediately
  if (typeof window.ensureScrollToTop === "function") {
    window.ensureScrollToTop(() => {
      initBadgesSection();
      initBadgesMobileSection();
    });
  } else {
    // Fallback: wait a bit for global.js to load
    setTimeout(() => {
      if (typeof window.ensureScrollToTop === "function") {
        window.ensureScrollToTop(() => {
          initBadgesSection();
          initBadgesMobileSection();
        });
      } else {
        initBadgesSection();
        initBadgesMobileSection();
      }
    }, 100);
  }
}

// Wait for DOM to be ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initialize);
} else {
  initialize();
}

// Handle browser back/forward navigation
window.addEventListener("pageshow", (event) => {
  if (event.persisted) {
    if (typeof window.ensureScrollToTop === "function") {
      window.ensureScrollToTop(() => {
        const section = document.querySelector(
          '.badges-desktop[id^="badges-section-"]'
        );
        if (section && typeof ScrollTrigger !== "undefined") {
          ScrollTrigger.getAll().forEach((trigger) => {
            if (trigger.trigger === section) {
              trigger.kill();
            }
          });
        }
        initBadgesSection();
        initBadgesMobileSection();
      });
    } else {
      const section = document.querySelector(
        '.badges-desktop[id^="badges-section-"]'
      );
      if (section && typeof ScrollTrigger !== "undefined") {
        ScrollTrigger.getAll().forEach((trigger) => {
          if (trigger.trigger === section) {
            trigger.kill();
          }
        });
      }
      initBadgesSection();
      initBadgesMobileSection();
    }
  }
});
